import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@pandiafi/design-system'
import { Modal, ContextNotification } from '@pandiafi/components'

import type { TimeSlotForResident } from '../../../common/validations/reservationCalendarSchemas'
import {
  formatDate,
  formatReservationDateTime,
  formatTimeRange,
} from '../../utils/dateUtils'
import type { ReservationCalendarForResident } from './types'
import { ResidentAppError } from '../../../common/error'
import { ResidentAppErrorCode } from '../../../common/types'
import { ReservationActionSuccess } from './ReservationActionSuccess'

import { useAPIGetReservationsSuspense } from 'api/apiHooks/useAPIGetReservations'
import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import { useAPICancelReservation } from 'api/apiHooks/useAPICancelReservation'

interface SingleReservationModalProps {
  show: boolean
  onClose: () => void
  calendar: ReservationCalendarForResident | null
  timeSlot: TimeSlotForResident | null
  readonly?: boolean
}

export const SingleReservationModal: React.FC<SingleReservationModalProps> = ({
  show,
  onClose,
  calendar,
  timeSlot,
  readonly = false,
}) => {
  const { t } = useTranslation('reservations')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const { currentContractId } = useCurrentContract()
  const [showSuccess, setShowSuccess] = useState(false)
  const [successAction, setSuccessAction] = useState<'cancel' | 'terminate'>(
    'cancel'
  )
  const { data: allReservations = [] } =
    useAPIGetReservationsSuspense(currentContractId)
  const cancelReservationMutation = useAPICancelReservation(currentContractId)

  useEffect(() => {
    if (show) {
      setErrorMsg(null)
      setShowSuccess(false)
    }
  }, [show])

  if (!calendar || !timeSlot) {
    return null
  }

  const ownReservation = timeSlot.reservations[0] || null
  if (
    !ownReservation ||
    ownReservation.reservationRecurrenceId !== null // Sanity check: this modal is intended for single reservations only.
  ) {
    return null
  }

  const reservationDetails = allReservations.find(
    (res) => res.id === ownReservation.id
  )

  if (!reservationDetails) {
    return null
  }

  const date = formatDate(reservationDetails.startsAt)
  const timeRange = formatTimeRange(
    reservationDetails.startsAt,
    reservationDetails.endsAt
  )

  const reservationDateTimeFormatted = formatReservationDateTime(
    reservationDetails.startsAt,
    reservationDetails.endsAt
  )

  const modalTitle = `${date} ${t('timeFormatting.atTime')} ${timeRange}`

  const canCancel = reservationDetails
    ? reservationDetails.hasPaytrailPayment === false
    : false

  const resolveErrorMessage = (err: unknown) => {
    if (err instanceof ResidentAppError) {
      switch (err.errorCode) {
        case ResidentAppErrorCode.RECURRING_RESERVATION_ALREADY_STARTED:
          return t('cancelModal.recurringCancelNotAllowed')
        case ResidentAppErrorCode.RECURRING_RESERVATION_TERMINATION_TODAY_NOT_ALLOWED:
          return t('cancelModal.terminateSameDayNotAllowed')
        default:
          break
      }
    }

    return t('cancelModal.cancelFailed')
  }

  const handleCancel = () => {
    setErrorMsg(null)
    cancelReservationMutation.mutate(
      {
        reservationId: ownReservation.id,
      },
      {
        onSuccess: () => {
          setSuccessAction('cancel')
          setShowSuccess(true)
        },
        onError: (err) => {
          setErrorMsg(resolveErrorMessage(err))
        },
      }
    )
  }

  const handleClose = () => {
    setShowSuccess(false)
    setErrorMsg(null)
    onClose()
  }

  return (
    <Modal
      show={show}
      onClose={showSuccess ? handleClose : onClose}
      title={t('cancelModal.reservationTitle')}
      subTitle={modalTitle}
      headerElement={
        showSuccess ? (
          <div>
            <div className="border border-gray-700 rounded-lg overflow-hidden">
              <ContextNotification type="success" textAlign="center">
                {t('timeFormatting.singleCancelled')}
              </ContextNotification>
            </div>
          </div>
        ) : undefined
      }
    >
      {showSuccess ? (
        <ReservationActionSuccess
          calendar={calendar}
          isRecurring={false}
          action={successAction}
          onClose={handleClose}
          reservation={{
            startsAt: reservationDetails.startsAt,
            endsAt: reservationDetails.endsAt,
          }}
        />
      ) : (
        <>
          <Modal.Content>
            <div className="space-y-4">
              <div>
                <div className="font-medium text-gray-900">{calendar.name}</div>
                <div className="text-gray-600">
                  {reservationDateTimeFormatted}
                </div>
              </div>

              {errorMsg ? (
                <ContextNotification type="error">
                  {errorMsg}
                </ContextNotification>
              ) : null}

              {!canCancel && (
                <ContextNotification type="info">
                  {t('cancelModal.singleNotAllowed')}
                </ContextNotification>
              )}
            </div>
          </Modal.Content>

          <Modal.Buttons>
            <Button variant="cancel" onClick={onClose}>
              {t('cancelModal.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleCancel}
              disabled={
                cancelReservationMutation.isPending || !canCancel || readonly
              }
              loading={cancelReservationMutation.isPending}
            >
              {t('cancelModal.confirmSingle')}
            </Button>
          </Modal.Buttons>
        </>
      )}
    </Modal>
  )
}
